/**
 * Reusable, user-facing copy for Cognito OAuth callback failures.
 */

import axios from "axios";

/** Rails `Company::CognitoUsersController` failure message. */
export const INVITE_INVALID_MESSAGE = "No valid invitation found.";

/** Matches `Company::CognitoUsersController` `error_code` values (split invite errors). */
export const COMPANY_INVITE_ERROR_CODES = [
  "ambiguous_invite_sources",
  "invite_required",
  "invite_required_no_expert_account",
  "subscription_invite_invalid",
  "company_email_invite_invalid",
  "company_email_mismatch",
] as const;
const COMPANY_INVITE_ERROR_CODE_SET = new Set<string>(
  COMPANY_INVITE_ERROR_CODES
);

/**
 * Machine-readable invite error from Rails JSON (`error_code`), or null if not an invite 422.
 */
export function getCompanyInviteErrorCode(err: unknown): string | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 422) return null;
  const data = err.response?.data;
  if (typeof data !== "object" || data === null) return null;
  const code = (data as { error_code?: unknown }).error_code;
  if (typeof code === "string" && COMPANY_INVITE_ERROR_CODE_SET.has(code)) {
    return code;
  }
  return null;
}

/** How to handle a Rails 422 invite error from the company Cognito callback (extend per backend `error_code`). */
export type CompanyInvite422Handling =
  | { strategy: "cognito_sign_in_fresh" }
  | { strategy: "company_app_root_sign_in" }
  | { strategy: "redirect_expert"; code: string };

/**
 * Next step after the company invite interstitial (timer / primary button).
 * Same mapping as {@link companyInvite422Handling}; keyed by `error_code` string only.
 */
export function companyInvite422HandlingFromCode(
  code: string
): CompanyInvite422Handling {
  const normalized = (code || "").trim() || "company_email_invite_invalid";

  switch (normalized) {
    case "company_email_mismatch":
      return { strategy: "cognito_sign_in_fresh" };
    case "invite_required_no_expert_account":
      return { strategy: "company_app_root_sign_in" };
    default:
      return { strategy: "redirect_expert", code: normalized };
  }
}

/**
 * Maps backend `error_code` on invite 422 responses. Default: interstitial then expert app with `company_invite_error`.
 * Add `case` branches when backend defines new codes.
 */
export function companyInvite422Handling(
  err: unknown
): CompanyInvite422Handling {
  const code = getCompanyInviteErrorCode(err) ?? "company_email_invite_invalid";
  return companyInvite422HandlingFromCode(code);
}

export type CognitoCallbackFriendlyError = {
  heading: string;
  detail: string;
};

/** Rails `/company/login` JSON: `{ errors }`, `{ message }`, etc. */
export function railsCompanyLoginErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data !== undefined) {
    const errorData = err.response.data;
    if (typeof errorData === "string") {
      return errorData;
    }
    if (typeof errorData === "object" && errorData !== null) {
      const d = errorData as Record<string, unknown>;
      if (Array.isArray(d.errors)) {
        return d.errors.map(String).join(", ");
      }
      if (typeof d.errors === "string") {
        return d.errors;
      }
      if (typeof d.errors === "object" && d.errors !== null) {
        const nested = d.errors as Record<string, unknown>;
        const first = Object.values(nested)[0];
        if (Array.isArray(first)) return first.map(String).join(", ");
        if (typeof first === "string") return first;
      }
      if (typeof d.error === "string") {
        return d.error;
      }
      if (typeof d.message === "string") {
        return d.message;
      }
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "An unexpected error occurred.";
}

/** Normalize Rails `errors` (string, array, or nested) to a single searchable string. */
function errorsFieldAsSearchText(errors: unknown): string {
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.map(String).join(" ");
  if (typeof errors === "object" && errors !== null) {
    return Object.values(errors as Record<string, unknown>)
      .map((v) => (Array.isArray(v) ? v.map(String).join(" ") : String(v)))
      .join(" ");
  }
  return "";
}

/**
 * 422 from company login when subscription / email invite context is invalid.
 * Matches `error_code` from Rails, legacy message, or substring "no valid invitation".
 */
export function isInviteInvalidError(err: unknown): boolean {
  if (getCompanyInviteErrorCode(err) !== null) return true;
  if (!axios.isAxiosError(err) || err.response?.status !== 422) return false;
  const data = err.response?.data;
  if (typeof data !== "object" || data === null || !("errors" in data)) {
    return false;
  }
  const raw = (data as { errors?: unknown }).errors;
  if (raw === INVITE_INVALID_MESSAGE) return true;
  const text = errorsFieldAsSearchText(raw).toLowerCase();
  return text.includes("no valid invitation");
}

function tryParseJsonObject(s: string): Record<string, unknown> | null {
  try {
    const o = JSON.parse(s);
    return typeof o === "object" && o !== null
      ? (o as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Maps Cognito `/oauth2/token` JSON (e.g. invalid_grant) to human copy.
 * Returns null if the body should fall through to generic handling.
 */
export function friendlyMessageForOAuthTokenJson(
  bodyText: string
): string | null {
  const parsed = tryParseJsonObject(bodyText.trim());
  const errorCode =
    typeof parsed?.error === "string" ? parsed.error.toLowerCase() : "";
  const textLower = bodyText.toLowerCase();

  if (errorCode === "invalid_grant" || textLower.includes("invalid_grant")) {
    return (
      "The sign-in step could not be completed. That usually means this page was refreshed, " +
      "opened more than once, or kept open too long, so the one-time link from sign-in is no longer valid.\n\n" +
      "What to do next: go back to the app and start sign in again from there."
    );
  }
  if (errorCode === "invalid_client" || textLower.includes("invalid_client")) {
    return "Something went wrong. Try the invite link again, or contact your account manager for help.";
  }
  if (
    errorCode === "invalid_request" ||
    textLower.includes('"error":"invalid_request"')
  ) {
    return "Something went wrong. Try the invite link again, or contact your account manager for help.";
  }
  if (errorCode === "unauthorized_client") {
    return "Something went wrong. Try the invite link again, or contact your account manager for help.";
  }
  return null;
}

/**
 * Heading + detail for callback error UI (Cognito OIDC, token exchange, Rails login).
 */
export function friendlySignInFailureDisplay(
  err: unknown
): CognitoCallbackFriendlyError {
  if (axios.isAxiosError(err) && err.response?.data !== undefined) {
    const detail = railsCompanyLoginErrorMessage(err);
    return {
      heading: "Invitation required",
      detail,
    };
  }

  const raw = err instanceof Error ? err.message : String(err);

  if (raw.includes("Token exchange failed:")) {
    const jsonPart = raw
      .slice(
        raw.indexOf("Token exchange failed:") + "Token exchange failed:".length
      )
      .trim();
    const fromOAuth = friendlyMessageForOAuthTokenJson(jsonPart);
    if (fromOAuth) {
      return {
        heading: "Sign-in couldn't be completed",
        detail: fromOAuth,
      };
    }
  }

  const fromRawJson = friendlyMessageForOAuthTokenJson(raw);
  if (fromRawJson) {
    return {
      heading: "Sign-in couldn't be completed",
      detail: fromRawJson,
    };
  }

  if (raw.toLowerCase().includes("invalid_grant")) {
    return {
      heading: "Sign-in couldn't be completed",
      detail:
        "Something went wrong. Try the invite link again, or contact your account manager for help. ",
    };
  }

  if (raw.includes("attributes required") || raw.includes("[email]")) {
    return {
      heading: "Email missing from your account",
      detail:
        "We couldn’t finish creating your session because your email address wasn’t provided by the sign-in provider. " +
        "Try signing in again, or ask your administrator if this continues.",
    };
  }

  if (
    raw.includes("No matching state") ||
    raw.includes("No state in response")
  ) {
    return {
      heading: "Sign-in session lost",
      detail:
        "Your browser couldn’t match this page to your sign-in request. Please start sign in again from the app.",
    };
  }

  if (raw.includes("No authorization code")) {
    return {
      heading: "Incomplete sign-in link",
      detail:
        "This address is missing part of the sign-in information. Open the app and start sign in again.",
    };
  }

  if (raw.includes("No access token")) {
    return {
      heading: "Couldn’t establish your session",
      detail:
        "We couldn’t get a secure session after sign-in. Please try signing in again.",
    };
  }

  const stripped = raw.replace(/^ErrorResponse:\s*/i, "").trim();
  if (stripped !== raw) {
    return friendlySignInFailureDisplay(new Error(stripped));
  }

  if (raw.length > 220 || (raw.includes("{") && raw.includes("}"))) {
    return {
      heading: "Something went wrong during sign-in",
      detail:
        "Please go back to the app and try signing in again. If this keeps happening, contact your administrator.",
    };
  }

  return {
    heading: "We couldn’t finish signing you in",
    detail: raw || "Please try again from the app.",
  };
}

/** Invite-specific copy when Rails returns 422 + INVITE_INVALID_MESSAGE (callback + email invite landing). */
export function inviteInvalidFriendlyError(): CognitoCallbackFriendlyError {
  return {
    heading: "Invite link is invalid",
    detail:
      "Invite link is expired or has already been used. Contact your account manager to request a new link.",
  };
}

/**
 * Heading + detail for the company-app interstitial before redirecting to the expert app
 * with `?company_invite_error=` (aligned with `slashexperts-frontend` `companyInviteErrorBanner.ts`).
 */
export function companyInviteErrorDisplayForCode(
  code: string
): CognitoCallbackFriendlyError {
  const trimmed = code.trim();
  const byCode: Record<string, CognitoCallbackFriendlyError> = {
    ambiguous_invite_sources: {
      heading: "Two invitation types detected",
      detail:
        "Only one company invitation method can be used at a time. Open a single invitation link from your email or subscription flow, then try again.",
    },
    invite_required: {
      heading: "Invitation required",
      detail:
        "To create a company account, open the link from your invitation email, then sign up with the email address the invite was sent to.",
    },
    invite_required_no_expert_account: {
      heading: "Company invitation required",
      detail:
        "A valid company invitation is required. Use company sign-in after you open your invitation link, or ask your program contact for access.",
    },
    subscription_invite_invalid: {
      heading: "Subscription invite is invalid",
      detail:
        "Invite link is expired or has already been used. Contact your account manager to request a new link.",
    },
    company_email_invite_invalid: {
      heading: "Invite link is invalid",
      detail:
        "Invite link is expired or has already been used. Contact your account manager to request a new link.",
    },
    company_email_mismatch: {
      heading: "Email does not match invitation",
      detail:
        "Sign up with the email address the invite was sent to. To use a different email, contact your account manager.",
    },
  };
  if (trimmed && byCode[trimmed]) {
    return byCode[trimmed];
  }
  return {
    heading: "Sign-in couldn't be completed",
    detail:
      "Something went wrong. Try the invite link again, or contact your account manager for help.",
  };
}
